import { apiClient } from './config.js';
import type {
  PaginatedResponse,
  EgressTargetSummary,
  EgressTargetDetail,
  PublishingHistoryEntry,
  PublishedArticleSummary,
  ArticlePublishTarget,
  PublishJobResponse,
  UnpublishJobsResponse,
  PublishRequestBody,
  UnpublishRequestBody,
  DeployStatus,
} from './types.js';

/**
 * List all egress targets with stats
 */
export async function listEgressTargets(): Promise<PaginatedResponse<EgressTargetSummary>> {
  const response = await apiClient.get<PaginatedResponse<EgressTargetSummary>>(
    '/admin/egress/targets'
  );
  return response.data;
}

/**
 * Get egress target detail with destinations and components
 */
export async function getEgressTarget(targetId: string): Promise<EgressTargetDetail> {
  const response = await apiClient.get<EgressTargetDetail>(
    `/admin/egress/targets/${targetId}`
  );
  return response.data;
}

/**
 * Get publishing history for a target
 */
export async function getTargetHistory(
  targetId: string,
  params?: { page?: number; limit?: number; status?: string; destination_id?: string }
): Promise<PaginatedResponse<PublishingHistoryEntry>> {
  const response = await apiClient.get<PaginatedResponse<PublishingHistoryEntry>>(
    `/admin/egress/targets/${targetId}/history`,
    { params }
  );
  return response.data;
}

/**
 * Get articles published to a target
 */
export async function getTargetArticles(
  targetId: string,
  params?: { page?: number; limit?: number }
): Promise<PaginatedResponse<PublishedArticleSummary>> {
  const response = await apiClient.get<PaginatedResponse<PublishedArticleSummary>>(
    `/admin/egress/targets/${targetId}/articles`,
    { params }
  );
  return response.data;
}

/**
 * Build/deploy state per destination for a `server_side` masthead.
 *
 * Reads the deploy fact we recorded when we caused it. The git-drift question
 * this replaced (GitHub `compare(main...test)`) was deleted with its route in
 * the §9.7 slim-down: on a server_side masthead the git branches are a
 * recoverable snapshot, not the thing that makes a page live.
 */
export async function getTargetDeployStatus(targetId: string): Promise<DeployStatus> {
  const response = await apiClient.get<DeployStatus>(
    `/admin/egress/targets/${targetId}/deploy-status`
  );
  return response.data;
}


export interface BuildRun {
  run_id: string;
  destination_type: 'test_branch' | 'main_branch';
  content_version: number | null;
  state: string;
  lane: string | null;
  commit_sha: string | null;
  cf_version_id: string | null;
  cf_kind: string | null;
  cf_preview_alias: string | null;
  pages_built: number | null;
  build_ms: number | null;
  deploy_ms: number | null;
  error_message: string | null;
  started_at: string | null;
  finished_at: string | null;
  deployed_at: string | null;
}

/**
 * Recent build+deploy runs for a masthead.
 *
 * `publication_build_runs` has recorded every build since migration 317 and
 * none of it has been visible. That is what made the 2026-08-26 OOM hard to
 * see: the dashboard showed "build-deploy returned 500" while the real cause
 * sat in this table.
 */
export async function getTargetBuildRuns(
  targetId: string,
  limit = 25,
): Promise<{ target_name: string; runs: BuildRun[] }> {
  const r = await apiClient.get(`/admin/egress/targets/${targetId}/build-runs?limit=${limit}`);
  return r.data as any;
}

export interface RebuildResult {
  success: boolean;
  target_name?: string;
  destination_type?: string;
  content_version?: number;
  job_id?: string | null;
  /** 'inserted' | 'coalesced' | 'pulled_forward' */
  outcome?: string;
  message?: string;
}

/**
 * Rebuild one destination without publishing anything.
 *
 * This is how a change that is not CONTENT reaches a live site — templates,
 * layouts, robots.txt, sitemap shape, CSS, .eleventy.js. The publish path never
 * sees those, and with Cloudflare Workers Builds disconnected a merge to main
 * builds nothing, so before this they shipped only when somebody happened to
 * promote an article for unrelated reasons.
 *
 * The build hard-resets to the branch's remote head, so it picks up whatever is
 * merged when it runs: MERGE FIRST, then rebuild.
 *
 * `outcome: 'coalesced'` means a build was already owed and this request joined
 * it. That is success — the owed build covers it — not a rejected click.
 */
export async function rebuildTarget(
  targetId: string,
  destinationType: 'test_branch' | 'main_branch'
): Promise<RebuildResult> {
  const response = await apiClient.post<RebuildResult>(
    `/admin/egress/targets/${targetId}/rebuild`,
    { destination_type: destinationType }
  );
  return response.data;
}

/**
 * Wholesale promote: open a PR `test → main` for this target's repo and
 * auto-merge. Ships every commit on test.
 *
 * ⚠ RETIRED FROM THE UI (D2). No surface calls this — the button lived in the
 * `cf_workers_builds` arm of the site page, which went with migration 323.
 * Kept only so the endpoint has a typed client while the backend route still
 * exists; the route itself goes in the §9.7 slim-down.
 *
 * ⚠ And the old doc-comment was wrong by 2026-08-26: it said "Cloudflare/11ty
 * rebuild on merge". Cloudflare Workers Builds is disconnected for every
 * masthead, so a merge to main builds NOTHING. What makes a promoted article
 * live is the next server-side build+deploy, not the merge.
 */
export async function promoteTarget(
  targetId: string
): Promise<{ success: boolean; pr_url?: string; pr_number?: number; message: string }> {
  const response = await apiClient.post(
    `/admin/egress/targets/${targetId}/promote`
  );
  return response.data;
}

/**
 * Approve an article for publishing.
 *
 * Without `opts`: flips a `draft` article to `approved` (existing behaviour).
 * With `{override: true, override_reason}`: flips a `review_blocked`
 * article to `approved` and records the operator override audit trail.
 * `override_reason` is required (min 10 chars).
 */
/** P8 (2026-10-06): return an EXPIRED article (held, never reviewed within the
 *  window) to the review queue. Content and Quality Gate judgement were kept
 *  intact while expired; the expiry is recorded in article_metadata.expiry_history. */
export async function unexpireArticle(articleId: string): Promise<Record<string, any>> {
  const response = await apiClient.post('/tools/unexpire', null, { params: { article_id: articleId } });
  return response.data;
}

export async function approveArticle(
  articleId: string,
  opts?: { override?: boolean; override_reason?: string }
): Promise<Record<string, any>> {
  const body = opts?.override
    ? { override: true, override_reason: opts.override_reason }
    : undefined;
  const response = await apiClient.post(`/articles/${articleId}/approve`, body);
  return response.data;
}

/**
 * Apply recorded Quality Gate fixes to an article via a focused copy-edit
 * pass. Runs an LLM that takes the article body + the QG `issues[].fix`
 * instructions and returns an edited markdown body. Marks the article
 * edited and flips `quality_gate_metadata.stale_after_edit`.
 */
export async function applyQualityGateFixes(
  articleId: string,
  expectedUpdatedAt: string | null = null,
): Promise<{ applied: boolean; article_id: string; fix_count: number; stale_after_edit: boolean; updated_at?: string }> {
  const response = await apiClient.post<{
    applied: boolean;
    article_id: string;
    fix_count: number;
    stale_after_edit: boolean;
    updated_at?: string;
  }>(`/articles/${articleId}/apply-fixes`, {
    expected_updated_at: expectedUpdatedAt,
  });
  return response.data;
}

/**
 * Preview a SELECTIVE QG fix batch — runs the copy-edit pass on only the
 * chosen issue indexes and returns the edited markdown. Nothing is saved;
 * discarding the preview costs nothing.
 */
export async function previewQgFixBatch(
  articleId: string,
  issueIndexes: number[],
  expectedUpdatedAt: string | null = null,
  /** The option the operator picked per issue index, when the finding offered
   *  alternatives. Omitted = use the finding's fix text as written. */
  optionKinds?: Record<string, string>,
): Promise<{
  article_id: string;
  issue_indexes: number[];
  new_content: string;
  new_faq: Array<{ question: string; answer: string }> | null;
  faq_before: Array<{ question: string; answer: string }> | null;
  base_updated_at: string;
}> {
  const response = await apiClient.post(`/articles/${articleId}/apply-fixes/preview`, {
    issue_indexes: issueIndexes,
    expected_updated_at: expectedUpdatedAt,
    option_kinds: optionKinds,
  });
  return response.data as any;
}

/**
 * Commit a previewed selective fix batch: persists the reviewed content,
 * records a content-history row, and stamps the applied issues (they stay
 * reported on the panel, badged as applied).
 */
export async function commitQgFixBatch(
  articleId: string,
  body: {
    new_content: string;
    issue_indexes: number[];
    expected_updated_at: string | null;
    new_faq?: Array<{ question: string; answer: string }> | null;
  },
): Promise<{
  committed: boolean;
  article_id: string;
  history_id: string;
  applied_issue_indexes: number[];
  updated_at: string;
}> {
  const response = await apiClient.post(`/articles/${articleId}/apply-fixes/commit`, body);
  return response.data as any;
}

/** Append-only content-change history (mig 237). */
export async function getArticleContentHistory(
  articleId: string,
): Promise<{ article_id: string; entries: import('./types.js').ContentHistoryEntry[] }> {
  const response = await apiClient.get(`/articles/${articleId}/content-history`);
  return response.data as any;
}

/** Restore the article content to before a history entry (recorded as a
 *  new rollback row — history is never mutated). */
export async function rollbackArticleContent(
  articleId: string,
  historyId: string,
  expectedUpdatedAt: string | null = null,
): Promise<{ rolled_back: boolean; history_id: string; updated_at: string }> {
  const response = await apiClient.post(
    `/articles/${articleId}/content-history/${historyId}/rollback`,
    { expected_updated_at: expectedUpdatedAt },
  );
  return response.data as any;
}

/**
 * Publish an article to a target
 */
export async function publishArticle(
  articleId: string,
  request: PublishRequestBody
): Promise<PublishJobResponse> {
  const response = await apiClient.post<PublishJobResponse>(
    `/articles/${articleId}/publish`,
    request
  );
  return response.data;
}

/**
 * Unpublish an article. With an empty request every live copy comes down
 * (main first) and the published URL is retired; target_name scopes to one
 * target; target + destination is the surgical escape hatch (WP-14).
 */
export async function unpublishArticle(
  articleId: string,
  request: UnpublishRequestBody = {}
): Promise<UnpublishJobsResponse> {
  const response = await apiClient.post<UnpublishJobsResponse>(
    `/articles/${articleId}/unpublish`,
    request
  );
  return response.data;
}

/**
 * Get publishing history for an article
 */
export async function getArticlePublishingHistory(
  articleId: string
): Promise<PublishingHistoryEntry[]> {
  const response = await apiClient.get<PublishingHistoryEntry[]>(
    `/articles/${articleId}/publishing-history`
  );
  return response.data;
}

/**
 * Get targets where an article is currently published
 */
export async function getArticlePublishTargets(
  articleId: string
): Promise<ArticlePublishTarget[]> {
  const response = await apiClient.get<ArticlePublishTarget[]>(
    `/articles/${articleId}/publish-targets`
  );
  return response.data;
}
